<template>
    <div class="page-customized">
        <workout-collection
            v-if="collect"
            used
            @unused="unused"
        ></workout-collection>

        <template v-else>
            <v-progress-linear
                v-if="overlay"
                absolute
                indeterminate
            ></v-progress-linear>

            <app-bar
                :title="collection.exists() ? t('collection_title_update') : t('collection_title_create')"
                back="/workout-collections"
            />

            <v-layout class="mt-n8">
                <v-card
                    width="100%"
                    color="transparent"
                    elevation="0"
                    class="pb-8"
                >
                    <v-card-text class="pb-0">
                        <v-form class="mt-6">
                            <div class="form-group-horizontal">
                                <div class="horizontal-label">{{ t('collection_label_name') }}</div>
                                <div class="horizontal-input">
                                    <v-text-field
                                        v-model="collection.name"
                                        type="text"
                                        required
                                        outlined
                                        dense
                                        :error-messages="form.errors.get('name')"
                                    ></v-text-field>
                                </div>
                                <div class="horizontal-actions">
                                    <edit-translations-modal
                                        v-model="collection.name"
                                        :items="collection.translations['name']"
                                        @touch="(v) => collection.translations['name'] = v"
                                    ></edit-translations-modal>
                                </div>
                            </div>
                            <div class="form-group-horizontal">
                                <div class="horizontal-label">{{ t('collection_label_description') }}</div>
                                <div class="horizontal-input">
                                    <v-textarea
                                        v-model="collection.description"
                                        required
                                        outlined
                                        dense
                                        :error-messages="form.errors.get('description')"
                                    ></v-textarea>
                                </div>
                                <div class="horizontal-actions">
                                    <edit-translations-modal
                                        v-model="collection.description"
                                        :items="collection.translations['description']"
                                        @touch="(v) => collection.translations['description'] = v"
                                        text
                                    ></edit-translations-modal>
                                </div>
                            </div>
                            <div class="form-group-horizontal">
                                <div class="horizontal-label">{{ t('collection_label_author') }}</div>
                                <div class="horizontal-input">
                                    <v-text-field
                                        v-model="collection.author"
                                        type="text"
                                        outlined
                                        dense
                                        :error-messages="form.errors.get('author')"
                                    ></v-text-field>
                                </div>
                                <div class="horizontal-actions">
                                    <edit-translations-modal
                                        v-model="collection.author"
                                        :items="collection.translations['author']"
                                        @touch="(v) => collection.translations['author'] = v"
                                    ></edit-translations-modal>
                                </div>
                            </div>
                            <div class="form-group-horizontal">
                                <div class="horizontal-label">{{ t('collection_label_video_url') }}</div>
                                <div class="horizontal-input">
                                    <v-text-field
                                        v-model="collection.videoUrl"
                                        type="text"
                                        outlined
                                        dense
                                        :error-messages="form.errors.get('video_url')"
                                    ></v-text-field>
                                </div>
                                <div class="horizontal-actions">
                                    <edit-translations-modal
                                        v-model="collection.videoUrl"
                                        :items="collection.translations['video_url']"
                                        @touch="(v) => collection.translations['video_url'] = v"
                                    ></edit-translations-modal>
                                </div>
                            </div>
                            <mux-playback-id-field
                                v-model="collection.muxPlaybackId"
                                :label="t('collection_label_mux_playback_id')"
                                :errors="form.errors.get('mux_playback_id')"
                            ></mux-playback-id-field>
                            <div class="form-group-horizontal">
                                <div class="horizontal-label">{{ t('collection_label_photo') }}</div>
                                <div class="horizontal-input">
                                    <v-file-input
                                        v-model="collection.photo"
                                        required
                                        outlined
                                        prepend-icon=""
                                        prepend-inner-icon="mdi-paperclip"
                                        :append-outer-icon="collection.photoUrl ? 'mdi-eye-outline' : ''"
                                        dense
                                        :error-messages="photoForm.errors.get('photo')"
                                        @click:append-outer="viewPhoto()"
                                        @click:clear="collection.photo = undefined"
                                    ></v-file-input>
                                </div>
                            </div>
                            <div class="form-group-horizontal">
                                <div class="horizontal-label">{{ t('collection_label_header_photo') }} *</div>
                                <div class="horizontal-input">
                                    <v-file-input
                                        v-model="collection.headerPhoto"
                                        required
                                        outlined
                                        prepend-icon=""
                                        prepend-inner-icon="mdi-paperclip"
                                        :append-outer-icon="collection.headerPhotoUrl ? 'mdi-eye-outline' : ''"
                                        dense
                                        :hint="t('collection_hint_header_photo')"
                                        persistent-hint
                                        :error-messages="headerPhotoError || photoForm.errors.get('header_photo')"
                                        @click:append-outer="viewHeaderPhoto()"
                                        @click:clear="collection.headerPhoto = undefined"
                                    ></v-file-input>
                                </div>
                            </div>
                            <div class="form-group-horizontal-cols">
                                <div class="form-group-horizontal">
                                    <div class="horizontal-label">{{ t('collection_label_level') }}</div>
                                    <div class="horizontal-input">
                                        <v-select
                                            v-model="collection.level"
                                            :items="levels()"
                                            required
                                            outlined
                                            append-icon="mdi-chevron-down"
                                            dense
                                            :error-messages="form.errors.get('level')"
                                        ></v-select>
                                    </div>
                                </div>
                                <div class="form-group-horizontal">
                                    <div class="horizontal-label">{{ t('collection_label_strokes') }}</div>
                                    <div class="horizontal-input">
                                        <v-select
                                            v-model="collection.strokeIds"
                                            :items="strokes"
                                            item-text="name"
                                            item-value="id"
                                            required
                                            outlined
                                            append-icon="mdi-chevron-down"
                                            dense
                                            multiple
                                            :error-messages="form.errors.get('stroke_ids')"
                                        ></v-select>
                                    </div>
                                </div>
                            </div>
                            <div class="form-group-horizontal-cols">
                                <div class="form-group-horizontal">
                                    <div class="horizontal-label">{{ t('collection_label_sort_order') }}</div>
                                    <div class="horizontal-input">
                                        <v-text-field
                                            v-model="collection.sortOrder"
                                            type="number"
                                            step="1"
                                            required
                                            outlined
                                            dense
                                            :error-messages="form.errors.get('sort_order')"
                                        ></v-text-field>
                                    </div>
                                </div>
                                <div class="form-group-horizontal">
                                    <div class="horizontal-label">{{ t('general_action_published') }}</div>
                                    <div class="horizontal-input">
                                        <v-switch
                                            class="ma-0"
                                            v-model="collection.enabled"
                                        ></v-switch>
                                    </div>
                                </div>
                            </div>

                            <div class="form-subtitle">{{ t('collection_label_audience') }}</div>
                            <div class="form-subtitle-description">{{ t('collection_hint_audience') }}</div>
                            <div
                                v-if="form.errors.consists('audience')"
                                class="red--text"
                            >{{ form.errors.get('audience') }}</div>

                            <v-simple-table class="transparent audience-table" dense>
                                <template v-slot:default>
                                    <thead>
                                        <tr>
                                            <th style="width: 200px"></th>
                                            <th
                                                v-for="attitude in audienceAttitudes()"
                                                :key="attitude.value"
                                                class="text-center"
                                                style="width: 150px"
                                            >{{ attitude.text }}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <template v-for="group in audienceGroups">
                                            <tr :key="group.title" class="audience-group">
                                                <td :colspan="audienceAttitudes().length + 1">{{ t(group.title) }}</td>
                                            </tr>
                                            <tr
                                                v-for="characteristic in group.characteristics"
                                                :key="characteristic"
                                            >
                                                <td class="titled">{{ audienceCharacteristicTitle(characteristic) }}</td>
                                                <td
                                                    v-for="attitude in audienceAttitudes()"
                                                    :key="attitude.value"
                                                    class="text-center"
                                                >
                                                    <v-icon
                                                        class="touched"
                                                        :color="collection.audience[characteristic] === attitude.value ? audienceAttitudeColor(attitude.value) : 'grey lighten-1'"
                                                        @click="toggleAudience(characteristic, attitude.value)"
                                                    >{{ collection.audience[characteristic] === attitude.value ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank' }}</v-icon>
                                                </td>
                                            </tr>
                                        </template>
                                    </tbody>
                                </template>
                            </v-simple-table>

                            <div class="form-group-horizontal">
                                <div class="horizontal-label">{{ t('collection_label_workouts') }}</div>
                                <div class="horizontal-input">
                                    <v-btn
                                        @click="collect = true"
                                        v-ripple="false"
                                        plain
                                        color="orange darken-4"
                                        class="add"
                                    >
                                        <v-icon left size="16">mdi-plus-circle</v-icon>{{ t('collection_workout_button_add') }}
                                    </v-btn>
                                    <span
                                        v-if="form.errors.consists('workouts')"
                                        class="red--text pl-2"
                                    >{{ form.errors.get('workouts') }}</span>
                                </div>
                            </div>

                            <v-simple-table class="transparent">
                                <template v-slot:default>
                                    <tbody>
                                        <tr v-if="collection.workouts.length === 0">
                                            <td class="text-center" colspan="6">{{ t('general_text_no_data') }}</td>
                                        </tr>
                                        <tr v-for="(workout, number) in collection.workouts">
                                            <td class="titled">{{ workout.id }}</td>
                                            <td class="titled">{{ workout.name }}</td>
                                            <td>{{ workout.stroke.name }}</td>
                                            <td>{{ levelTitle(workout.level) }}</td>
                                            <td>{{ time.format(workout.workoutTotalTime) }}</td>
                                            <td class="text-center">
                                                <v-menu
                                                    offset-y
                                                    left
                                                >
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-btn
                                                            icon
                                                            v-bind="attrs"
                                                            v-on="on"
                                                        >
                                                            <v-icon>mdi-dots-vertical</v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <v-list dense>
                                                        <v-list-item
                                                            v-if="number > 0"
                                                            @click.prevent="moveWorkout(number, number - 1)"
                                                        >
                                                            <v-list-item-title
                                                                class="customized-primary--text"
                                                            >{{ t('general_action_move_up') }}</v-list-item-title>
                                                        </v-list-item>
                                                        <v-list-item
                                                            v-if="number < collection.workouts.length - 1"
                                                            @click.prevent="moveWorkout(number, number + 1)"
                                                        >
                                                            <v-list-item-title
                                                                class="customized-primary--text"
                                                            >{{ t('general_action_move_down') }}</v-list-item-title>
                                                        </v-list-item>
                                                        <v-list-item
                                                            @click.prevent="delWorkout(number)"
                                                        >
                                                            <v-list-item-title
                                                                class="orange--text"
                                                            >{{ t('general_action_delete') }}</v-list-item-title>
                                                        </v-list-item>
                                                    </v-list>
                                                </v-menu>
                                            </td>
                                        </tr>
                                    </tbody>
                                </template>
                            </v-simple-table>
                        </v-form>
                    </v-card-text>

                    <v-card-actions class="px-4">
                        <v-btn
                            :to="{ name: 'workout-collections' }"
                            class="mr-4"
                            color="primary"
                        >{{ t('general_action_cancel') }}</v-btn>
                        <v-btn
                            :loading="form.busy"
                            :disabled="form.busy"
                            color="orange"
                            @click.prevent="submit"
                        >{{ collection.exists() ? t('general_action_save') : t('collection_title_create') }}</v-btn>
                    </v-card-actions>
                </v-card>
            </v-layout>
        </template>
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent, onMounted, reactive, ref, toRefs } from '@vue/composition-api'
    import { useI18n } from 'vue-i18n-composable'
    import { useStore } from '@/store'
    import { useRouter } from '@/composition-api'
    import { useForm } from '@/core/form'
    import { useEnums } from '@/core/defaults'
    import { WorkoutCollectionModel } from '@/core/models/workout-collection'
    import { Workout } from '@/core/models/workout'
    import { Stroke } from '@/core/models/stroke'
    import * as time from '@/core/utils/time-helper'
    import EditTranslationsModal from '@/components/EditTranslationsModal.vue'
    import WorkoutCollection from '@/components/workouts/WorkoutCollection.vue'
    import MuxPlaybackIdField from '@/components/MuxPlaybackIdField.vue'

    // Presentation-only grouping of the audience characteristics — the backend enum stays flat.
    // Any characteristic missing here still shows up, under "Other".
    const AUDIENCE_GROUPS = [
        { title: 'collection_audience_group_gender', characteristics: ['male', 'female'] },
        { title: 'collection_audience_group_level', characteristics: ['beginner', 'intermediate', 'advanced', 'competition'] },
        { title: 'collection_audience_group_life_stage', characteristics: ['pregnant', 'menopause', 'senior'] },
        { title: 'collection_audience_group_environment', characteristics: ['swim_pool', 'swim_open_water'] },
        { title: 'collection_audience_group_swimmer_type', characteristics: ['triathlete', 'open_water_swimmer', 'fitness_swimmer', 'health_swimmer'] },
        { title: 'collection_audience_group_favourite_stroke', characteristics: ['freestyle_favourite', 'backstroke_favourite', 'breaststroke_favourite', 'butterfly_favourite', 'medley_favourite'] },
    ]

    export default defineComponent({
        components: {
            MuxPlaybackIdField,
            EditTranslationsModal,
            WorkoutCollection,
        },

        setup(props) {
            const store = useStore()
            const router = useRouter()
            const i18n = useI18n()
            const id = router.currentRoute.params['id'] || undefined
            const captured = router.currentRoute.query['id'] || undefined

            const state = reactive({
                overlay: true,
                collect: false,
            })

            const collectionModel = computed(() => <WorkoutCollectionModel>store.getters['workoutCollectionItem/get'])

            const strokes = computed(() => store.getters['strokeCollection/get'].data.map((item: Stroke) => {
                return { id: item.id, name: item.name }
            }))

            const enums = useEnums()

            const audienceGroups = computed(() => {
                const known = AUDIENCE_GROUPS.flatMap(group => group.characteristics)
                const rest = enums.audienceCharacteristics()
                    .map((item: any) => item.value)
                    .filter((value: string) => ! known.includes(value))
                return rest.length
                    ? [...AUDIENCE_GROUPS, { title: 'collection_audience_group_other', characteristics: rest }]
                    : AUDIENCE_GROUPS
            })

            // Vue 2 cannot track added/removed keys on a plain object — replace the map instead.
            const setAudience = (characteristic: string, attitude: string | null) => {
                const audience = { ...collectionModel.value.audience }
                if (attitude) {
                    audience[characteristic] = attitude
                } else {
                    delete audience[characteristic]
                }
                collectionModel.value.audience = audience
            }

            // Radio semantics: clicking the option that is already selected clears it back to "not set".
            const toggleAudience = (characteristic: string, attitude: string) => {
                setAudience(characteristic, collectionModel.value.audience[characteristic] === attitude ? null : attitude)
            }

            const form = reactive(useForm())
            const photoForm = reactive(useForm())
            // The header image is required by SSO-3561, but images are uploaded by a separate
            // endpoint after the record is saved, so the rule can only live here.
            const headerPhotoError = ref('')

            const capture = () => {
                store.dispatch('workoutCollectionItem/getOrNew', {
                    id: (id || captured),
                    payload: { append: 'translations', relations: 'workouts,strokes' }
                }).then(() => {
                    state.overlay = false
                })
            }

            const unused = (workouts: Workout[] = []) => {
                collectionModel.value.addWorkouts(workouts)
                state.collect = false
            }

            const moveWorkout = (currentIndex: number, index: number) => {
                collectionModel.value.moveWorkout(currentIndex, index)
            }

            const delWorkout = (index: number) => {
                collectionModel.value.delWorkout(index)
            }

            const submit = () => {
                headerPhotoError.value = ''
                if (! collectionModel.value.headerPhoto && ! collectionModel.value.headerPhotoUrl) {
                    headerPhotoError.value = <string>i18n.t('collection_error_header_photo_required')
                    return
                }
                if (captured) {
                    collectionModel.value.id = <any>undefined
                }
                form.usingSubmit(store.dispatch('workoutCollectionItem/updateOrCreate', {
                    id: id,
                    payload: collectionModel.value.payload()
                }))
                    .then(() => {
                        const newId = <any>collectionModel.value.id
                        const next = () => router.push({ name: 'workout-collections.view', params: <any>{ id: newId } })
                        store.dispatch('notify', { color: 'success', message: i18n.t('general_notice_data_updated') })
                        // Both images ride on the same upload endpoint, so one request carries
                        // whichever of them the user picked.
                        if (collectionModel.value.photo || collectionModel.value.headerPhoto) {
                            const data = new FormData()
                            if (collectionModel.value.photo) {
                                data.append('photo', collectionModel.value.photo)
                            }
                            if (collectionModel.value.headerPhoto) {
                                data.append('header_photo', collectionModel.value.headerPhoto)
                            }
                            photoForm.usingSubmit(store.dispatch('workoutCollectionItem/photo', { id: newId, payload: data }).then(() => {
                                next()
                            }))
                        } else {
                            next()
                        }
                    })
            }

            const viewPhoto = () => {
                window.open(collectionModel.value.photoUrl)
            }

            const viewHeaderPhoto = () => {
                window.open(collectionModel.value.headerPhotoUrl)
            }

            onMounted(() => {
                store.dispatch('strokeCollection/get', { sort: 'sort_order', type: 'primary' })
                capture()
            })

            return {
                form,
                photoForm,
                headerPhotoError,
                viewPhoto,
                viewHeaderPhoto,
                submit,
                unused,
                moveWorkout,
                delWorkout,
                time,
                collection: collectionModel.value,
                strokes,
                audienceGroups,
                toggleAudience,
                ...useEnums(),
                ...useI18n(),
                ...toRefs(state)
            }
        }
    })
</script>

<style scoped>
    .audience-table {
        max-width: 800px;
        margin: 20px 0 36px;
    }

    .audience-table .audience-group td {
        font-weight: 600;
        padding-top: 26px;
        border-bottom: none;
    }
</style>
